import React, { useState, useEffect, useRef } from "react";
import qs from "qs";
import { Table, Pagination, Modal } from "../../components";
import EntityContainer from "@/modules/entity/containers";
import ModulesActions from "@/modules/entity/actions";
import { useDispatch } from "react-redux";
import { get } from "lodash";
import UseAlert from "../../hooks/useAlert";
import StoreActions from "../../store/actions/system";
import { Check, X, FileText, File } from "lucide-react";
import { Tag, Select } from "antd";
import moment from "moment";
import { constants } from "../../services";

const APPROVABLE_TYPE_LABELS = {
  [constants.TYPE_ZIMZIM]: "Zim-Zim",
  [constants.TYPE_TRADE]: "Trade",
  [constants.TYPE_HR]: "HR",
  [constants.TYPE_LOOP]: "Loop",
};

const getApprovableTypeLabel = (type) =>
  APPROVABLE_TYPE_LABELS[Number(type)] != null
    ? APPROVABLE_TYPE_LABELS[Number(type)]
    : String(type != null ? type : "—");

const isLeadRow = (row) =>
  row &&
  row.approvable_type &&
  String(row.approvable_type)
    .toLowerCase()
    .includes("lead");

const isTaskRow = (row) =>
  row &&
  row.approvable_type &&
  !String(row.approvable_type)
    .toLowerCase()
    .includes("lead");

const getStatusLabel = (statusValue) => {
  if (statusValue === -1 || statusValue === "-1")
    return { color: "red", label: "Отклонено" };
  if (statusValue === 0 || statusValue === "0")
    return { color: "gold", label: "Ожидание" };
  if (statusValue === 1 || statusValue === "1")
    return { color: "green", label: "Подтверждено" };
  return { color: "default", label: "—" };
};

const APPROVABLE_TYPE_TASK = "Modules\\Employee\\Models\\Task\\Task";
const APPROVABLE_TYPE_LEAD = "Modules\\Employee\\Models\\Lead\\Lead";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const AUDIO_EXTS = ["m4a", "mp3", "wav", "ogg", "aac"];
const VIDEO_EXTS = ["mp4", "webm", "mov", "avi"];

const getFileUrl = (f) => {
  if (!f || !f.domain) return "";
  const base = (f.domain || "").replace(/\/$/, "");
  const folder = (f.folder || "").replace(/^\//, "");
  const file = f.file || f.slug || "";
  return folder ? `${base}/${folder}${file}` : `${base}/${file}`;
};

const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const ModalFiles = ({ files }) => {
  if (!files || !Array.isArray(files) || files.length === 0) return null;
  return (
    <div>
      <div
        style={{
          color: "#64748b",
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        Файлы
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {files.map((f) => {
          const ext = (f.ext || "").toLowerCase();
          const url = getFileUrl(f);
          const title = f.title || f.description || f.file || "Файл";
          const isImage = IMAGE_EXTS.includes(ext);
          const isAudio = AUDIO_EXTS.includes(ext);
          const isVideo = VIDEO_EXTS.includes(ext);
          const isPdf = ext === "pdf";
          return (
            <div
              key={f.id || f.file || title}
              style={{
                background: "#f8fafc",
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                width: isImage ? 140 : "100%",
                maxWidth: isImage ? 140 : 320,
              }}
            >
              {isImage && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block" }}
                >
                  <img
                    src={url}
                    alt={title}
                    style={{
                      width: "100%",
                      height: 100,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </a>
              )}
              {isAudio && (
                <div style={{ padding: 12 }}>
                  <audio
                    controls
                    style={{ width: "100%", height: 36, marginBottom: 8 }}
                    src={url}
                  />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 12,
                      color: "#0ea5e9",
                      wordBreak: "break-all",
                    }}
                  >
                    {title}
                  </a>
                </div>
              )}
              {isVideo && (
                <div style={{ padding: 8 }}>
                  <video
                    controls
                    style={{ width: "100%", maxHeight: 180, borderRadius: 4 }}
                    src={url}
                  />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 12,
                      color: "#0ea5e9",
                      display: "block",
                      marginTop: 6,
                      wordBreak: "break-all",
                    }}
                  >
                    {title}
                  </a>
                </div>
              )}
              {(isPdf || (!isImage && !isAudio && !isVideo)) && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 12,
                    color: "#1e293b",
                    textDecoration: "none",
                  }}
                >
                  <div style={{ flexShrink: 0, color: "#64748b" }}>
                    {isPdf ? <FileText size={28} /> : <File size={28} />}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </div>
                    {f.size != null && (
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        {formatFileSize(f.size)}
                      </div>
                    )}
                  </div>
                </a>
              )}
              {isImage && (
                <div style={{ padding: "6px 10px" }}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 12,
                      color: "#0ea5e9",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {title}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Approvals = ({ location, history }) => {
  const dispatch = useDispatch();
  const { showAlert } = UseAlert();

  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { page = 1, status, tab } = query;
  const tabKey = tab === "lead" ? "lead" : "task";

  const [canUpdate, setCanUpdate] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    type: null,
    row: null,
    comment: "",
  });
  const [leadDetailModal, setLeadDetailModal] = useState({
    open: false,
    row: null,
  });
  const [taskDetailModal, setTaskDetailModal] = useState({
    open: false,
    row: null,
  });
  const justClosedRef = useRef(false);

  useEffect(() => {
    dispatch(StoreActions.SetToolbarTitle("Модерация задач"));
  }, [dispatch]);

  const handlePageClick = (selectedPage) => {
    const search = { ...query, page: selectedPage + 1 };
    history.push({
      search: qs.stringify(search),
    });
  };

  const openConfirmModal = (row, type) => {
    setConfirmState({
      open: true,
      type,
      row,
    });
  };

  const closeConfirmModal = () => {
    justClosedRef.current = true;
    setTimeout(() => {
      justClosedRef.current = false;
    }, 200);
    setConfirmState({
      open: false,
      type: null,
      row: null,
      comment: "",
    });
  };

  const openLeadDetailModal = (row) => {
    setLeadDetailModal({ open: true, row });
  };

  const closeLeadDetailModal = () => {
    setLeadDetailModal({ open: false, row: null });
  };

  const handleApproveRejectFromLeadModal = (row, type) => {
    closeLeadDetailModal();
    openConfirmModal(row, type);
  };

  const openTaskDetailModal = (row) => {
    setTaskDetailModal({ open: true, row });
  };

  const closeTaskDetailModal = () => {
    setTaskDetailModal({ open: false, row: null });
  };

  const handleApproveRejectFromTaskModal = (row, type) => {
    closeTaskDetailModal();
    openConfirmModal(row, type);
  };

  const handleAction = (row, type, comment) => {
    if (!row || !row.id) return;
    const id = row.id;
    const url =
      type === "approve"
        ? `/approvals/confirm/${id}`
        : `/approvals/reject/${id}`;

    setActionLoading({ id, type });
    dispatch(
      ModulesActions.FormDefault.request({
        method: "put",
        entity: "approvals",
        name: "all",
        id,
        url,
        data: { content: comment || "" },
        cb: {
          success: () => {
            showAlert(
              type === "approve" ? "Задача одобрена" : "Задача отклонена",
              { type: "success" },
            );
            closeConfirmModal();
          },
          error: (response) => {
            const errorMessage = get(response, "errorMessage", "");
            showAlert(errorMessage || "Ошибка действия", { type: "danger" });
          },
          finally: () => {
            setCanUpdate((prev) => !prev);
            setActionLoading(null);
          },
        },
      }),
    );
  };

  const renderCellText = (value) =>
    value != null && value !== "" ? value : "-";

  const handleStatusFilterChange = (nextStatus) => {
    const newQuery = { ...query, page: 1 };

    if (nextStatus === undefined || nextStatus === null || nextStatus === "") {
      delete newQuery.status;
    } else {
      newQuery.status = nextStatus;
    }

    history.push({
      search: qs.stringify(newQuery),
    });
  };

  const handleTabChange = (nextTab) => {
    if (nextTab === tabKey) return;
    const newQuery = { ...query, page: 1, tab: nextTab };
    history.push({ search: qs.stringify(newQuery) });
  };

  return (
    <div className="d-panel">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center" style={{ gap: 24 }}>
          <div className="secondary-title mb-0">Модерация задач</div>
          <div className="bb-tab-buttons">
            <div
              className={`bb-tab-buttons__item ${
                tabKey === "task" ? "active" : ""
              }`}
              onClick={() => handleTabChange("task")}
            >
              Задача
            </div>
            <div
              className={`bb-tab-buttons__item ${
                tabKey === "lead" ? "active" : ""
              }`}
              onClick={() => handleTabChange("lead")}
            >
              Лид
            </div>
          </div>
        </div>
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Фильтр по статусу"
          value={
            status !== undefined && status !== null && status !== ""
              ? String(status)
              : undefined
          }
          onChange={(value) => handleStatusFilterChange(value)}
        >
          <Select.Option value="-1">Отклонено</Select.Option>
          <Select.Option value="0">Ожидание</Select.Option>
          <Select.Option value="1">Подтверждено</Select.Option>
        </Select>
      </div>

      <Modal size={400} isOpen={confirmState.open} toggle={closeConfirmModal}>
        <div style={{ padding: "8px" }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: 16,
            }}
          >
            {confirmState.type === "approve"
              ? "Вы точно хотите одобрить?"
              : "Вы точно хотите отклонить?"}
          </div>
          <textarea
            rows={4}
            placeholder="Комментарий..."
            value={confirmState.comment}
            onChange={(e) =>
              setConfirmState((prev) => ({ ...prev, comment: e.target.value }))
            }
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 14,
              resize: "vertical",
              outline: "none",
              marginBottom: 20,
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={closeConfirmModal}
              disabled={!!actionLoading}
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: "#fff",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={!!actionLoading}
              onClick={() =>
                handleAction(
                  confirmState.row,
                  confirmState.type,
                  confirmState.comment,
                )
              }
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                border: "none",
                background:
                  confirmState.type === "approve" ? "#0f9d58" : "#d93025",
                color: "#fff",
                cursor: actionLoading ? "default" : "pointer",
                fontSize: 14,
                fontWeight: 500,
                opacity: actionLoading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {!!actionLoading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              {confirmState.type === "approve" ? "Одобрить" : "Отклонить"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        size={500}
        isOpen={leadDetailModal.open}
        toggle={closeLeadDetailModal}
      >
        {leadDetailModal.row && (
          <div style={{ padding: "4px 0" }}>
            <div
              className="modal-title"
              style={{
                marginBottom: 20,
                paddingBottom: 16,
                borderBottom: "1px solid #e8e8e8",
                fontSize: 18,
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              Лид — данные
            </div>
            <div
              style={{
                background: "#f8fafc",
                borderRadius: 8,
                padding: "16px 20px",
                marginBottom: 24,
              }}
            >
              {[
                {
                  label: "Название",
                  value: renderCellText(
                    get(leadDetailModal.row, "approvable.customer_name"),
                  ),
                },
                {
                  label: "Телефон",
                  value: renderCellText(
                    get(leadDetailModal.row, "approvable.phone"),
                  ),
                },
                {
                  label: "Регион",
                  value: renderCellText(
                    get(leadDetailModal.row, "approvable.region.name"),
                  ),
                },
                {
                  label: "Район",
                  value: renderCellText(
                    get(leadDetailModal.row, "approvable.district.name"),
                  ),
                },
                {
                  label: "Тип",
                  value: getApprovableTypeLabel(
                    get(leadDetailModal.row, "approvable.type"),
                  ),
                },
              ].map(({ label, value }, idx, arr) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    padding: "8px 0",
                    borderBottom:
                      idx < arr.length - 1 ? "1px solid #e2e8f0" : "none",
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: 14,
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      color: "#1e293b",
                      fontSize: 14,
                      textAlign: "right",
                      wordBreak: "break-word",
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 24, padding: "16px 20px" }}>
              <ModalFiles
                files={get(leadDetailModal.row, "approvable.files")}
              />
            </div>
            {(leadDetailModal.row.status === 0 ||
              leadDetailModal.row.status === "0") && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                  padding: 16,
                }}
              >
                <button
                  type="button"
                  className="flex items-center justify-center"
                  style={{
                    minHeight: 40,
                    paddingLeft: 20,
                    paddingRight: 20,
                    borderRadius: 8,
                    border: "none",
                    cursor: actionLoading ? "default" : "pointer",
                    backgroundColor: "#0f9d58",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 8,
                    opacity: actionLoading ? 0.8 : 1,
                  }}
                  disabled={!!actionLoading}
                  onClick={() =>
                    handleApproveRejectFromLeadModal(
                      leadDetailModal.row,
                      "approve",
                    )
                  }
                  title="Одобрить"
                >
                  {actionLoading &&
                  actionLoading.id === leadDetailModal.row.id &&
                  actionLoading.type === "approve" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <Check size={18} />
                  )}
                  <span>Одобрить</span>
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center"
                  style={{
                    minHeight: 40,
                    paddingLeft: 20,
                    paddingRight: 20,
                    borderRadius: 8,
                    border: "none",
                    cursor: actionLoading ? "default" : "pointer",
                    backgroundColor: "#d93025",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 8,
                    opacity: actionLoading ? 0.8 : 1,
                  }}
                  disabled={!!actionLoading}
                  onClick={() =>
                    handleApproveRejectFromLeadModal(
                      leadDetailModal.row,
                      "reject",
                    )
                  }
                  title="Отклонить"
                >
                  {actionLoading &&
                  actionLoading.id === leadDetailModal.row.id &&
                  actionLoading.type === "reject" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <X size={18} />
                  )}
                  <span>Отклонить</span>
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        size={500}
        isOpen={taskDetailModal.open}
        toggle={closeTaskDetailModal}
      >
        {taskDetailModal.row && (
          <div style={{ padding: "4px 0" }}>
            <div
              className="modal-title"
              style={{
                marginBottom: 20,
                paddingBottom: 16,
                borderBottom: "1px solid #e8e8e8",
                fontSize: 18,
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              Задача — данные
            </div>
            <div
              style={{
                background: "#f8fafc",
                borderRadius: 8,
                padding: "16px 20px",
                marginBottom: 24,
              }}
            >
              {(() => {
                const row = taskDetailModal.row;
                const createdRaw = row.created_at;
                const createdStr =
                  createdRaw && moment(createdRaw).isValid()
                    ? moment(createdRaw).format("DD-MM-YYYY HH:mm")
                    : "—";
                const statusVal =
                  row.status !== undefined && row.status !== null
                    ? row.status
                    : null;
                const statusConfig = getStatusLabel(statusVal);
                const rows = [
                  { label: "Дата создания", value: createdStr },
                  { label: "Тип", value: "Задача" },
                  {
                    label: "Пользователь",
                    value: renderCellText(
                      get(row, "user.name") || get(row, "approvable.user.name"),
                    ),
                  },
                  {
                    label: "Организация",
                    value: renderCellText(
                      get(row, "organization.name") ||
                        get(row, "approvable.organization.name"),
                    ),
                  },
                  {
                    label: "Регион",
                    value: renderCellText(
                      get(row, "organization.region.name") ||
                        get(row, "approvable.organization.region.name"),
                    ),
                  },
                  {
                    label: "Название задачи",
                    value: renderCellText(get(row, "approvable.name")),
                  },
                  {
                    label: "Описание",
                    value: renderCellText(get(row, "approvable.description")),
                  },
                  {
                    label: "Услуга",
                    value: renderCellText(get(row, "approvable.service.name")),
                  },
                  //   { label: "Статус", value: statusConfig.label },
                  {
                    label: "Старый статус",
                    value: get(row, "change_for_humans.old"),
                  },
                  {
                    label: "Обновление статуса",
                    value: get(row, "change_for_humans.new"),
                  },
                ];
                return rows.map(({ label, value }, idx, arr) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      padding: "8px 0",
                      borderBottom:
                        idx < arr.length - 1 ? "1px solid #e2e8f0" : "none",
                      gap: 12,
                    }}
                  >
                    <span
                      style={{
                        color: "#64748b",
                        fontSize: 14,
                        fontWeight: 500,
                        flexShrink: 0,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        color: "#1e293b",
                        fontSize: 14,
                        textAlign: "right",
                        wordBreak: "break-word",
                      }}
                    >
                      {label === "Статус" ||
                      label === "Старый статус" ||
                      label === "Обновление статуса" ? (
                        <Tag color={statusConfig.color}>{value}</Tag>
                      ) : (
                        value
                      )}
                    </span>
                  </div>
                ));
              })()}
            </div>
            <ModalFiles files={get(taskDetailModal.row, "approvable.files")} />
            {(taskDetailModal.row.status === 0 ||
              taskDetailModal.row.status === "0" ||
              taskDetailModal.row.status === 1 ||
              taskDetailModal.row.status === "1") && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "flex-end",
                  padding: 16,
                }}
              >
                {(taskDetailModal.row.status === 0 ||
                  taskDetailModal.row.status === "0") && (
                  <button
                    type="button"
                    className="flex items-center justify-center"
                    style={{
                      minHeight: 40,
                      paddingLeft: 20,
                      paddingRight: 20,
                      borderRadius: 8,
                      border: "none",
                      cursor: actionLoading ? "default" : "pointer",
                      backgroundColor: "#0f9d58",
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 500,
                      gap: 8,
                      opacity: actionLoading ? 0.8 : 1,
                    }}
                    disabled={!!actionLoading}
                    onClick={() =>
                      handleApproveRejectFromTaskModal(
                        taskDetailModal.row,
                        "approve",
                      )
                    }
                    title="Одобрить"
                  >
                    {actionLoading &&
                    actionLoading.id === taskDetailModal.row.id &&
                    actionLoading.type === "approve" ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <Check size={18} />
                    )}
                    <span>Одобрить</span>
                  </button>
                )}
                <button
                  type="button"
                  className="flex items-center justify-center"
                  style={{
                    minHeight: 40,
                    paddingLeft: 20,
                    paddingRight: 20,
                    borderRadius: 8,
                    border: "none",
                    cursor: actionLoading ? "default" : "pointer",
                    backgroundColor: "#d93025",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 8,
                    opacity: actionLoading ? 0.8 : 1,
                  }}
                  disabled={!!actionLoading}
                  onClick={() =>
                    handleApproveRejectFromTaskModal(
                      taskDetailModal.row,
                      "reject",
                    )
                  }
                  title="Отклонить"
                >
                  {actionLoading &&
                  actionLoading.id === taskDetailModal.row.id &&
                  actionLoading.type === "reject" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <X size={18} />
                  )}
                  <span>Отклонить</span>
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <EntityContainer.All
        entity="approvals"
        name="all"
        url="/approvals"
        primaryKey="id"
        canUpdate={canUpdate}
        params={{
          page,
          sort: query.sort || "-id",
          limit: 50,
          include:
            "approvable.region,approvable.district,approvable.files,approvable.service,approvable.organization.region,approvable.user",
          extra: {
            append: "change_for_humans",
          },
          filter: {
            approvable_type:
              tabKey === "lead" ? APPROVABLE_TYPE_LEAD : APPROVABLE_TYPE_TASK,
            ...(status !== undefined &&
              status !== null &&
              status !== "" && { status }),
          },
        }}
      >
        {({ items, isFetched, meta }) => (
          <div className="with-pagination with-bg">
            <Table
              items={items}
              isFetched={isFetched}
              rowKey="id"
              onRowClick={(item) => {
                if (justClosedRef.current) return;
                if (tabKey === "lead") openLeadDetailModal(item);
                else openTaskDetailModal(item);
              }}
              columns={
                tabKey === "lead"
                  ? [
                      {
                        title: "№",
                        dataIndex: "id",
                        className: "ws-no-wrap w-100 cursor-pointer",
                        render: (v, row, index) => (
                          <div className="divider-wrapper fw-600">
                            {index + 1}
                          </div>
                        ),
                      },
                      {
                        title: "Дата создания",
                        dataIndex: "created_at",
                        render: (v, row) => {
                          const raw = row.created_at || v;
                          if (!raw) return "-";
                          const m = moment(raw);
                          return m.isValid() ? (
                            <div style={{ whiteSpace: "nowrap" }}>
                              {m.format("DD-MM-YYYY HH:mm")}
                            </div>
                          ) : (
                            "-"
                          );
                        },
                      },
                      {
                        title: "Название",
                        dataIndex: "customer_name",
                        render: (v, row) => (
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {renderCellText(
                              get(row, "approvable.customer_name"),
                            )}
                          </div>
                        ),
                      },
                      {
                        title: "Телефон",
                        dataIndex: "phone",
                        render: (v, row) =>
                          renderCellText(get(row, "approvable.phone")),
                      },
                      {
                        title: "Регион",
                        dataIndex: "region",
                        render: (v, row) =>
                          renderCellText(get(row, "approvable.region.name")),
                      },
                      {
                        title: "Район",
                        dataIndex: "district",
                        render: (v, row) =>
                          renderCellText(get(row, "approvable.district.name")),
                      },
                      {
                        title: "Тип",
                        dataIndex: "type",
                        render: (v, row) =>
                          getApprovableTypeLabel(get(row, "approvable.type")),
                      },
                      {
                        title: "Статус",
                        dataIndex: "status",
                        render: (v, row) => {
                          const c = getStatusLabel(
                            row.status != null ? row.status : v,
                          );
                          return <Tag color={c.color}>{c.label}</Tag>;
                        },
                      },
                      {
                        title: "Изменение",
                        dataIndex: "change_for_humans",
                        render: (_, row) => {
                          const oldVal = get(row, "change_for_humans.old");
                          const newVal = get(row, "change_for_humans.new");
                          if (!oldVal && !newVal) return "-";
                          const oldC = getStatusLabel(oldVal);
                          const newC = getStatusLabel(newVal);
                          return (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tag color={oldC.color}>{oldVal || "—"}</Tag>
                              <span style={{ color: "#94a3b8", fontSize: 14 }}>
                                →
                              </span>
                              <Tag color={newC.color}>{newVal || "—"}</Tag>
                            </div>
                          );
                        },
                      },
                      {
                        title: "Действия",
                        dataIndex: "actions",
                        render: (v, row) => {
                          const sv = row.status != null ? row.status : v;
                          const isApp =
                            actionLoading &&
                            actionLoading.id === row.id &&
                            actionLoading.type === "approve";
                          const isRej =
                            actionLoading &&
                            actionLoading.id === row.id &&
                            actionLoading.type === "reject";
                          if (!(sv === 0 || sv === "0")) return null;
                          return (
                            <div className="flex" style={{ gap: 6 }}>
                              <button
                                type="button"
                                title="Одобрить"
                                className="flex items-center justify-center"
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: "none",
                                  padding: 0,
                                  cursor:
                                    isApp || isRej ? "default" : "pointer",
                                  backgroundColor: "#0f9d58",
                                  color: "#fff",
                                  opacity: isApp || isRej ? 0.6 : 1,
                                }}
                                disabled={isApp || isRej}
                                onClick={() => openConfirmModal(row, "approve")}
                              >
                                {isApp ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <Check size={16} />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Отклонить"
                                className="flex items-center justify-center"
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: "none",
                                  padding: 0,
                                  cursor:
                                    isApp || isRej ? "default" : "pointer",
                                  backgroundColor: "#d93025",
                                  color: "#fff",
                                  opacity: isApp || isRej ? 0.6 : 1,
                                }}
                                disabled={isApp || isRej}
                                onClick={() => openConfirmModal(row, "reject")}
                              >
                                {isRej ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <X size={16} />
                                )}
                              </button>
                            </div>
                          );
                        },
                      },
                    ]
                  : [
                      {
                        title: "№",
                        dataIndex: "id",
                        className: "ws-no-wrap w-100 cursor-pointer",
                        render: (v, row, index) => (
                          <div className="divider-wrapper fw-600">
                            {index + 1}
                          </div>
                        ),
                      },
                      {
                        title: "ID",
                        dataIndex: "id",
                        className: "ws-no-wrap w-100 cursor-pointer",
                        render: (v, row) => (
                          <div className="divider-wrapper fw-600">{row.id}</div>
                        ),
                      },
                      {
                        title: "Дата создания",
                        dataIndex: "created_at",
                        render: (v, row) => {
                          const raw = row.created_at || v;
                          if (!raw) return "-";
                          const m = moment(raw);
                          return m.isValid() ? (
                            <div style={{ whiteSpace: "nowrap" }}>
                              {m.format("DD-MM-YYYY HH:mm")}
                            </div>
                          ) : (
                            "-"
                          );
                        },
                      },
                      {
                        title: "Пользователь",
                        dataIndex: "user",
                        render: (v, row) => (
                          <div style={{ whiteSpace: "nowrap" }}>
                            {renderCellText(
                              get(row, "user.name") ||
                                get(row, "approvable.user.name"),
                            )}
                          </div>
                        ),
                      },
                      {
                        title: "Организация",
                        dataIndex: "organization",
                        render: (v, row) => (
                          <div
                            style={{ minWidth: 200, whiteSpace: "pre-wrap" }}
                          >
                            {renderCellText(
                              get(row, "organization.name") ||
                                get(row, "approvable.organization.name"),
                            )}
                          </div>
                        ),
                      },
                      {
                        title: "Регион",
                        dataIndex: "region",
                        render: (v, row) =>
                          renderCellText(
                            get(row, "organization.region.name") ||
                              get(row, "approvable.organization.region.name"),
                          ),
                      },
                      {
                        title: "Название задачи",
                        dataIndex: "approvable_name",
                        render: (v, row) => (
                          <div
                            style={{ minWidth: 250, whiteSpace: "pre-wrap" }}
                          >
                            {renderCellText(get(row, "approvable.name"))}
                          </div>
                        ),
                      },
                      {
                        title: "Описание",
                        dataIndex: "approvable_description",
                        render: (v, row) => (
                          <div
                            style={{ minWidth: 250, whiteSpace: "pre-wrap" }}
                          >
                            {renderCellText(get(row, "approvable.description"))}
                          </div>
                        ),
                      },
                      {
                        title: "Услуга",
                        dataIndex: "service_name",
                        render: (v, row) => (
                          <div
                            style={{ minWidth: 200, whiteSpace: "pre-wrap" }}
                          >
                            {renderCellText(
                              get(row, "approvable.service.name"),
                            )}
                          </div>
                        ),
                      },
                      {
                        title: "Статус",
                        dataIndex: "status",
                        render: (v, row) => {
                          const c = getStatusLabel(
                            row.status != null ? row.status : v,
                          );
                          return <Tag color={c.color}>{c.label}</Tag>;
                        },
                      },
                      {
                        title: "Изменение",
                        dataIndex: "change_for_humans",
                        render: (_, row) => {
                          const oldVal = get(row, "change_for_humans.old");
                          const newVal = get(row, "change_for_humans.new");
                          if (!oldVal && !newVal) return "-";
                          const oldC = getStatusLabel(oldVal);
                          const newC = getStatusLabel(newVal);
                          return (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tag color={oldC.color}>{oldVal || "—"}</Tag>
                              <span style={{ color: "#94a3b8", fontSize: 14 }}>
                                →
                              </span>
                              <Tag color={newC.color}>{newVal || "—"}</Tag>
                            </div>
                          );
                        },
                      },
                      {
                        title: "Действия",
                        dataIndex: "actions",
                        render: (v, row) => {
                          const sv = row.status != null ? row.status : v;
                          const isApp =
                            actionLoading &&
                            actionLoading.id === row.id &&
                            actionLoading.type === "approve";
                          const isRej =
                            actionLoading &&
                            actionLoading.id === row.id &&
                            actionLoading.type === "reject";
                          if (!(sv === 0 || sv === "0")) return null;
                          return (
                            <div className="flex" style={{ gap: 6 }}>
                              <button
                                type="button"
                                title="Одобрить"
                                className="flex items-center justify-center"
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: "none",
                                  padding: 0,
                                  cursor:
                                    isApp || isRej ? "default" : "pointer",
                                  backgroundColor: "#0f9d58",
                                  color: "#fff",
                                  opacity: isApp || isRej ? 0.6 : 1,
                                }}
                                disabled={isApp || isRej}
                                onClick={() => openConfirmModal(row, "approve")}
                              >
                                {isApp ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <Check size={16} />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Отклонить"
                                className="flex items-center justify-center"
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: "none",
                                  padding: 0,
                                  cursor:
                                    isApp || isRej ? "default" : "pointer",
                                  backgroundColor: "#d93025",
                                  color: "#fff",
                                  opacity: isApp || isRej ? 0.6 : 1,
                                }}
                                disabled={isApp || isRej}
                                onClick={() => openConfirmModal(row, "reject")}
                              >
                                {isRej ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <X size={16} />
                                )}
                              </button>
                            </div>
                          );
                        },
                      },
                    ]
              }
            />

            {get(meta, "pageCount", 1) > 1 && (
              <Pagination
                pageCount={get(meta, "pageCount", 1)}
                currentPage={page ? Number(page) : 1}
                handlePageClick={handlePageClick}
              />
            )}
          </div>
        )}
      </EntityContainer.All>
    </div>
  );
};

export default Approvals;
